import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import * as T from './types';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { AnimationType, GraphicInterface } from '../../../../stores/reducers/graphics';
import { editOverlayDraft, updateOverlayInAllShotOverlays } from '../../../../connection/graphics';
import { SelectGroupOption } from '../../../general-components/select-group';
import * as GraphicsActions from '../../../../stores/actions/graphics';
import { getAnimationName } from '../../../../utils/custom-graphics';
import classNames from 'classnames';
import useShotMemos from '../../../../hooks/use-shot-memos';
import { upsertAssociatedContent } from '../../../../stores/actions/generate-content';

export interface AnimationsInterface {
	handleShowForDurationChange: (durationInSeconds: number) => void;
	handleAnimationInChange: (choice: T.animationTiming) => void;
	handlePreviewAnimation: (isOut: boolean) => void;
	isPreviewAnimationPlaying: boolean;
	selectedInAnimation: SelectGroupOption;
	setSelectedInAnimation: (animation: SelectGroupOption) => void;
	selectedInDuration: SelectGroupOption;
	handleInOutDurationChange: (nDuration: number, isOut: boolean) => void;
	selectedInDirection: number;
	setSelectedInDirection: (direction: number) => void;
	selectedOutAnimation: SelectGroupOption;
	setSelectedOutAnimation: (animation: SelectGroupOption) => void;
	selectedOutDuration: SelectGroupOption;
	setSelectedOutDuration: (duration: SelectGroupOption) => void;
	selectedOutDirection: number,
	setSelectedOutDirection: (direction: number) => void;
	setShowFor: (showFor: number) => void;
	showFor: number;
	delayInDuration: number;
	selectedInTransition: SelectGroupOption;
	setDelayInDuration: (delay: number) => void;
}

export const AnimationsContext = React.createContext({
	handleShowForDurationChange: (durationInSeconds: number) => { },
	handleAnimationInChange: (choice: T.animationTiming) => { },
	handlePreviewAnimation: (isOut: boolean) => { },
	isPreviewAnimationPlaying: false,
	selectedInAnimation: T.animationOptions[0],
	setSelectedInAnimation: (animation: SelectGroupOption) => { },
	selectedInDuration: T.durationOptions[0],
	handleInOutDurationChange: (nDuration: number, isOut: boolean) => { },
	selectedInDirection: 0,
	setSelectedInDirection: (direction: number) => { },
	selectedOutAnimation: T.animationOutOptions[0],
	setSelectedOutAnimation: (animation: SelectGroupOption) => { },
	selectedOutDuration: T.durationOptions[0],
	setSelectedOutDuration: (duration: SelectGroupOption) => { },
	selectedOutDirection: 0,
	setSelectedOutDirection: (direction: number) => { },
	setShowFor: (direction: number) => { },
	showFor: 5,
	delayInDuration: 0,
	selectedInTransition: T.transitionEffects[0],
	setDelayInDuration: (delay: number) => { }

}) as React.Context<AnimationsInterface>;

interface Props {
	children: React.JSX.Element,
	isForShotEditorModal?: boolean
}
export const AnimationsProvider = (
	props: Props
):  React.ReactElement<any, React.Provider<AnimationsInterface>> => {
	const { isForShotEditorModal, children } = props;

	const dispatch = useDispatch();

	const {
		currentGraphicId,
		graphics,
		isPreviewAnimationPlaying,
	} = useSelector((state: GS) => state.GraphicsReducer);
	const shotModalGraphics = useSelector(
		(state: GS) => state.GraphicsReducer.shotModalGraphics,
	);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const grUser = useSelector((state: GS) => state.SessionReducer.user?.gr_user);
	const enabledOverlays = useSelector(
		(state: GS) => state.GraphicsReducer.enabledOverlays,
	);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const currentShotId = useSelector((state: GS) => state.ShotsReducer.currentShotId);

	let selectedGraphic = isForShotEditorModal
		// eslint-disable-next-line max-len
		? shotModalGraphics.find((g) => g.id === currentGraphicId) || graphics.find((g) => g.id === currentGraphicId)
		: graphics.find((g) => g.id === currentGraphicId);

	if (!selectedGraphic) { // if there is no selectedGraphic pick the first one
		selectedGraphic = isForShotEditorModal
			? shotModalGraphics[0] ?? { animation: {} }
			: graphics[0] ?? { animation: {} };
	}

	const {
		animation = {
			id: 0,
			name: '',
			build_in: 0,
			build_out: 0,
			in_animation: 0,
			in_direction: 0,
			in_duration: 0,
			out_animation: 0,
			out_direction: 0,
			out_duration: 0,
		},
	} = selectedGraphic;

	const [selectedInTransition, setSelectedInTransition] = useState(
		T.transitionEffects[animation.build_in ?? 0],
	);
	const [selectedInAnimation, setSelectedInAnimation] = useState(
		T.animationOptions[animation.in_animation ?? 0],
	);
	const [selectedInDuration, setSelectedInDuration] = useState(
		T.durationOptions[animation.in_duration ?? 0],
	);
	const [selectedInDirection, setSelectedInDirection] = useState(
		animation.in_direction ?? 0,
	);
	const [selectedOutTransition, setSelectedOutTransition] = useState(
		T.transitionOutEffects[animation.build_out ?? 0],
	);
	const [selectedOutAnimation, setSelectedOutAnimation] = useState(
		T.animationOutOptions[animation.out_animation ?? 0],
	);
	const [selectedOutDuration, setSelectedOutDuration] = useState(
		T.durationOptions[animation.out_duration ?? 0],
	);
	const [selectedOutDirection, setSelectedOutDirection] = useState(
		animation.out_direction ?? 0,
	);
	// const [currentAnimationData, setCurrentAnimationData] = useState(animation);
	const prevAnimation = useRef<any>({});
	const [showFor, setShowFor] = useState(animation?.show_for ?? 0);
	const [delayInDuration, setDelayInDuration] = useState(animation?.delay_in ?? 0);

	const { isShotBuilderOpen } = useShotMemos();

	useEffect(() => {
		// @ts-ignore
		window.ignoreSave = true;
		setSelectedInTransition(T.transitionEffects[animation.build_in ?? 0]);
		setSelectedInAnimation(T.animationOptions[animation.in_animation ?? 0]);
		setSelectedInDuration(T.durationOptions[animation.in_duration ?? 0]);
		setSelectedInDirection(animation.in_direction ?? 0);
		setSelectedOutTransition(T.transitionOutEffects[animation.build_out ?? 0]);
		setSelectedOutAnimation(T.animationOutOptions[animation.out_animation ?? 0]);
		setSelectedOutDuration(T.durationOptions[animation.out_duration ?? 0]);
		prevAnimation.current = animation;
		setShowFor(animation?.show_for ?? 0);
		setDelayInDuration(animation?.delay_in ?? 0);
		const tim = setTimeout(() => {
			// @ts-ignore
			window.ignoreSave = false;
			clearTimeout(tim);
		}, 500);
	}, [currentGraphicId]);

	// save the updates
	useEffect(() => {
		// @ts-ignore
		if (window?.ignoreSave === true) {
			return;
		}

		const updatedAnimation: AnimationType = {
			name: animation.name,
			id: animation.id,
			build_in: T.transitionEffects.findIndex((o) => o.value === selectedInTransition.value),
			build_out: T.transitionOutEffects.findIndex((o) => o.value === selectedOutTransition.value),
			in_animation: T.animationOptions.findIndex((o) => o === selectedInAnimation),
			in_direction: selectedInDirection,
			in_duration: T.durationOptions.findIndex((o) => o === selectedInDuration),
			out_animation: T.animationOutOptions.findIndex((o) => o === selectedOutAnimation),
			out_direction: selectedOutDirection,
			out_duration: T.durationOptions.findIndex((o) => o === selectedOutDuration),
			show_for: showFor,
			delay_in: delayInDuration,
		};

		const keys = Object.keys(updatedAnimation);

		// lets not send allow them to DoS our database
		let noChange = true;
		keys.forEach((key) => {
			// @ts-ignore
			if (prevAnimation.current[key] !== updatedAnimation[key]) {
				noChange = false;
			}
		});
		if (!noChange) {
			prevAnimation.current = updatedAnimation
			if (isForShotEditorModal) {
				const newGraphic: any = { ...selectedGraphic, animation: updatedAnimation };
				editOverlayDraft(token ?? '', newGraphic);
			} else if (token) {
				const updatedGraphic: GraphicInterface | undefined = selectedGraphic
					? { ...selectedGraphic, animation: updatedAnimation }
					: undefined;

				if (updatedGraphic && meeting?.meeting && grUser) {
					dispatch(upsertAssociatedContent(token, meeting?.meeting, grUser, updatedGraphic, currentShotId))
				}

				if (meeting?.meeting && updatedGraphic?.id) {
					updateOverlayInAllShotOverlays(
						token,
						meeting.meeting,
						updatedGraphic.id,
						!(selectedInTransition.value === 'manual'),
					);
				}
			}
		}
	}, [
		selectedInTransition,
		selectedInAnimation,
		selectedInDuration,
		selectedInDirection,
		selectedOutTransition,
		selectedOutAnimation,
		selectedOutDuration,
		selectedOutDirection,
		showFor,
		delayInDuration,
		meeting,
		grUser
	]);

	const isSelectedGraphicEnabled = useMemo((): boolean => {
		if (enabledOverlays?.length && selectedGraphic?.id) {
			return enabledOverlays.includes(selectedGraphic.id);
		}

		return false;
	}, [enabledOverlays, selectedGraphic]);

	const handlePreviewAnimation = useCallback((isOut: boolean) => {
		const triggeredAnimation = isOut ? selectedOutAnimation : selectedInAnimation;

		const shotEditor = isShotBuilderOpen ? '-shot-editor' : '';

		const video = document.getElementById(`custom-graphic-vid-${currentGraphicId}${shotEditor}`) as HTMLVideoElement;
		if (!isOut && video) {
			video.currentTime = 0;
			video.play();
		}

		if (!animation || triggeredAnimation.value === 'none') return;
		const obj = document.getElementById(`graphic-wrapper-${currentGraphicId}${shotEditor}`);
		const child = document.getElementById(`custom-graphic-${currentGraphicId}${shotEditor}`);
		const resizeBox = obj?.parentNode?.childNodes[1] as HTMLElement;

		const duration = isOut ? selectedOutDuration : selectedInDuration;

		if (obj) {
			dispatch(GraphicsActions.previewAnimationIsPlaying(true));
			obj.className = classNames(
				'graphic-wrapper',
				selectedGraphic
					? getAnimationName(selectedGraphic, isOut)
					: '',
			);
			if (child && triggeredAnimation.value === 'move-in' && isSelectedGraphicEnabled) {
				child.style.transform = `rotate(${selectedGraphic?.rotate ?? 0}deg)`;
			}
			if (resizeBox) {
				resizeBox.style.display = 'none';
			}
			setTimeout(() => { // reset it so we can play again
				obj.className = 'graphic-wrapper';
				if (child && isSelectedGraphicEnabled) {
					child.style.transform = '';
				}
				if (resizeBox) {
					resizeBox.style.display = 'block';
				}
				dispatch(GraphicsActions.previewAnimationIsPlaying(false));
			}, (duration.value * 1000) - 100);
		}
	}, [
		selectedOutAnimation,
		selectedInAnimation,
		isShotBuilderOpen,
		currentGraphicId,
		animation,
		selectedOutDuration,
		selectedInDuration,
		isSelectedGraphicEnabled
	]);

	const handleShowForDurationChange = (durationInSeconds: number) => {
		if (durationInSeconds === -1) {
			setSelectedOutTransition(T.transitionOutEffects[0]); //"manual"
		} else {
			setShowFor(durationInSeconds);
			setSelectedOutTransition(T.transitionOutEffects[1]); //"after-delay"
		}
	};

	const handleAnimationInChange = (choice: T.animationTiming) => {
		if (choice === T.animationTiming.immediate) {
			setSelectedInTransition(T.transitionEffects[0]);
		} else if (choice === T.animationTiming.manual) {
			setSelectedInTransition(T.transitionEffects[1]);
		} else if (choice === T.animationTiming.delayed) {
			setSelectedInTransition(T.transitionEffects[2]);
		}
	}

	const handleInOutDurationChange = (nDuration: number, isOut: boolean) => {
		if (isOut) {
			setSelectedOutDuration(T.durationOptions.find((op) => op.value === nDuration) ?? T.durationOptions[0]);
		} else {
			setSelectedInDuration(T.durationOptions.find((op) => op.value === nDuration) ?? T.durationOptions[0]);
		}
	}

	return <AnimationsContext.Provider value={{
		handleShowForDurationChange,
		handleAnimationInChange,
		handlePreviewAnimation,
		isPreviewAnimationPlaying,
		selectedInAnimation,
		setSelectedInAnimation,
		selectedInDuration,
		handleInOutDurationChange,
		selectedInDirection,
		setSelectedInDirection,
		selectedOutAnimation,
		setSelectedOutAnimation,
		selectedOutDuration,
		setSelectedOutDuration,
		selectedOutDirection,
		setSelectedOutDirection,
		setShowFor,
		showFor,
		delayInDuration,
		setDelayInDuration,
		selectedInTransition
	}}>
		{children}
	</AnimationsContext.Provider>
};